import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import * as d3 from "d3";
import type {
  ShapBeeswarmFeature,
  ShapBeeswarmFeaturePoint,
} from "../../utils/performanceDriversModelFit";
import { debug as logDebug } from "../../utils/console";

export type { ShapBeeswarmFeature, ShapBeeswarmFeaturePoint };

interface ShapBeeswarmProps {
  features: ShapBeeswarmFeature[];
  title?: string;
  targetUnit?: string;
}

const VERTICAL_LAYOUT_BREAKPOINT_PX = 1000;
const MIN_COLUMN_WIDTH_PX = 56;
const MIN_ROW_HEIGHT_PX = 52;
const POINT_RADIUS = 3;
const BEESWARM_STEP = POINT_RADIUS * 2 + 0.25;
const DEFAULT_SHAP_DOMAIN: [number, number] = [-5, 5];

function rankedFeatureAxisLabel(features: ShapBeeswarmFeature[], key: string | number): string {
  const name = String(key);
  const idx = features.findIndex((f) => f.name === name);
  const feat = idx >= 0 ? features[idx] : undefined;
  const label = feat?.label ?? name;
  return idx >= 0 ? `#${idx + 1} ${label}` : label;
}

function appendBeeswarmRankingCaption(
  root: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  width: number,
): void {
  root.append("text")
    .attr("class", "shap-beeswarm-ranking-caption")
    .attr("x", width - 8)
    .attr("y", 41)
    .attr("text-anchor", "end")
    .attr("dominant-baseline", "middle")
    .text("Importance Ranking (Higher -> Lower)");
}

function readCssColor(container: HTMLElement | undefined, token: string, fallback: string): string {
  if (!container) return fallback;
  const value = getComputedStyle(container).getPropertyValue(token).trim();
  return value || fallback;
}

function normColorScale(colorLow: string, colorMid: string, colorHigh: string): (norm: number) => string {
  return d3
    .scaleLinear<string>()
    .domain([0, 0.5, 1])
    .range([colorLow, colorMid, colorHigh])
    .clamp(true);
}

type PlacedBeeswarmPoint = ShapBeeswarmFeaturePoint & { x: number; y: number; isOutlier: boolean };

interface ShapYScaleConfig {
  domain: [number, number];
  clipLow: number;
  clipHigh: number;
}

const SHAP_PERCENTILE_LOW = 0.0125;
const SHAP_PERCENTILE_HIGH = 0.9875;
const OUTLIER_EDGE_OFFSET_PX = POINT_RADIUS + 1;

/** Y domain spanning the central 97.5% of SHAP values (1.25th–98.75th percentile). */
function resolveShapYScaleConfig(features: ShapBeeswarmFeature[]): ShapYScaleConfig {
  const values = features
    .flatMap((feat) => feat.points.map((p) => p.shap))
    .sort(d3.ascending);

  if (values.length === 0) {
    return {
      domain: DEFAULT_SHAP_DOMAIN,
      clipLow: DEFAULT_SHAP_DOMAIN[0],
      clipHigh: DEFAULT_SHAP_DOMAIN[1],
    };
  }

  const clipLow = d3.quantileSorted(values, SHAP_PERCENTILE_LOW) ?? values[0];
  const clipHigh = d3.quantileSorted(values, SHAP_PERCENTILE_HIGH) ?? values[values.length - 1];
  const span = clipHigh - clipLow;
  const pad = span > 0 ? span * 0.02 : 0.25;

  return {
    domain: [clipLow - pad, clipHigh + pad],
    clipLow,
    clipHigh,
  };
}

function mapShapToPlotY(
  shap: number,
  yScale: d3.ScaleLinear<number, number>,
  clipLow: number,
  clipHigh: number,
  innerH: number,
): { y: number; isOutlier: boolean } {
  if (shap < clipLow) {
    return { y: innerH - OUTLIER_EDGE_OFFSET_PX, isOutlier: true };
  }
  if (shap > clipHigh) {
    return { y: OUTLIER_EDGE_OFFSET_PX, isOutlier: true };
  }
  return { y: yScale(shap), isOutlier: false };
}

function mapShapToPlotX(
  shap: number,
  xScale: d3.ScaleLinear<number, number>,
  clipLow: number,
  clipHigh: number,
  innerW: number,
): { x: number; isOutlier: boolean } {
  if (shap < clipLow) {
    return { x: OUTLIER_EDGE_OFFSET_PX, isOutlier: true };
  }
  if (shap > clipHigh) {
    return { x: innerW - OUTLIER_EDGE_OFFSET_PX, isOutlier: true };
  }
  return { x: xScale(shap), isOutlier: false };
}

/**
 * Horizontal beeswarm at the exact SHAP y-position.
 * Spread within the column when space allows; otherwise overlap at center.
 */
function layoutColumnBeeswarm(
  points: ShapBeeswarmFeaturePoint[],
  centerX: number,
  columnHalfWidth: number,
  yForPoint: (pt: ShapBeeswarmFeaturePoint) => { y: number; isOutlier: boolean },
): PlacedBeeswarmPoint[] {
  const sorted = [...points].sort((a, b) => a.shap - b.shap || a.norm - b.norm);
  const placed: PlacedBeeswarmPoint[] = [];
  const maxDxSteps = Math.max(1, Math.ceil(columnHalfWidth / BEESWARM_STEP));

  for (const pt of sorted) {
    const { y, isOutlier } = yForPoint(pt);
    let x = centerX;

    for (let dxStep = 0; dxStep <= maxDxSteps; dxStep += 1) {
      const dxValues = dxStep === 0 ? [0] : [dxStep * BEESWARM_STEP, -dxStep * BEESWARM_STEP];
      let placedThisStep = false;

      for (const dx of dxValues) {
        const candidateX = centerX + dx;
        if (Math.abs(candidateX - centerX) > columnHalfWidth) continue;

        const rowTaken = placed.some(
          (p) => Math.abs(p.y - y) < 0.5 && Math.abs(p.x - candidateX) < BEESWARM_STEP * 0.9
        );
        if (!rowTaken) {
          x = candidateX;
          placedThisStep = true;
          break;
        }
      }
      if (placedThisStep) break;
    }

    placed.push({ ...pt, x, y, isOutlier });
  }

  return placed;
}

/**
 * Vertical beeswarm at the exact SHAP x-position.
 * Spread within the row when space allows; otherwise overlap at center.
 */
function layoutRowBeeswarm(
  points: ShapBeeswarmFeaturePoint[],
  centerY: number,
  rowHalfHeight: number,
  xForPoint: (pt: ShapBeeswarmFeaturePoint) => { x: number; isOutlier: boolean },
): PlacedBeeswarmPoint[] {
  const sorted = [...points].sort((a, b) => a.shap - b.shap || a.norm - b.norm);
  const placed: PlacedBeeswarmPoint[] = [];
  const maxDySteps = Math.max(1, Math.ceil(rowHalfHeight / BEESWARM_STEP));

  for (const pt of sorted) {
    const { x, isOutlier } = xForPoint(pt);
    let y = centerY;

    for (let dyStep = 0; dyStep <= maxDySteps; dyStep += 1) {
      const dyValues = dyStep === 0 ? [0] : [dyStep * BEESWARM_STEP, -dyStep * BEESWARM_STEP];
      let placedThisStep = false;

      for (const dy of dyValues) {
        const candidateY = centerY + dy;
        if (Math.abs(candidateY - centerY) > rowHalfHeight) continue;

        const columnTaken = placed.some(
          (p) => Math.abs(p.x - x) < 0.5 && Math.abs(p.y - candidateY) < BEESWARM_STEP * 0.9
        );
        if (!columnTaken) {
          y = candidateY;
          placedThisStep = true;
          break;
        }
      }
      if (placedThisStep) break;
    }

    placed.push({ ...pt, x, y, isOutlier });
  }

  return placed;
}

export default function ShapBeeswarm(props: ShapBeeswarmProps) {
  let containerRef: HTMLDivElement | undefined;
  let svgRef: SVGSVGElement | undefined;
  const [containerWidth, setContainerWidth] = createSignal(720);

  const sortedFeatures = createMemo(() =>
    [...(props.features ?? [])].sort((a, b) => b.mean_abs_shap - a.mean_abs_shap)
  );

  const targetUnit = () => props.targetUnit ?? "VMG%";
  const isVerticalLayout = createMemo(() => containerWidth() < VERTICAL_LAYOUT_BREAKPOINT_PX);

  onMount(() => {
    if (!containerRef) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 720;
      setContainerWidth(width);
    });
    observer.observe(containerRef);
    setContainerWidth(containerRef.clientWidth || 720);
    onCleanup(() => observer.disconnect());
  });

  createEffect(() => {
    const features = sortedFeatures();
    const vertical = isVerticalLayout();
    if (!svgRef || !containerRef || features.length === 0) return;

    const shapScaleConfig = resolveShapYScaleConfig(features);
    const clipLow = shapScaleConfig.clipLow;
    const clipHigh = shapScaleConfig.clipHigh;
    const availableWidth = containerWidth();

    const width = vertical
      ? Math.max(availableWidth, 320)
      : Math.max(availableWidth, features.length * MIN_COLUMN_WIDTH_PX + 96, 720);
    const height = vertical
      ? Math.max(360, features.length * MIN_ROW_HEIGHT_PX + 150)
      : 440;
    const margin = vertical
      ? { top: 28, right: 12, bottom: 56, left: 112 }
      : { top: 28, right: 12, bottom: 96, left: 56 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const svg = d3.select(svgRef);
    svg.selectAll("*").remove();

    const colorLow = readCssColor(containerRef, "--shap-beeswarm-color-low", "#1a4a85");
    const colorMid = readCssColor(containerRef, "--shap-beeswarm-color-mid", "#d1d5db");
    const colorHigh = readCssColor(containerRef, "--shap-beeswarm-color-high", "#962626");
    const colorScale = normColorScale(colorLow, colorMid, colorHigh);

    const defs = svg.append("defs");
    defs
      .append("clipPath")
      .attr("id", "shap-beeswarm-plot-clip")
      .append("rect")
      .attr("width", innerW)
      .attr("height", innerH);

    const root = svg
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    appendBeeswarmRankingCaption(root, width);

    const g = root.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    g.append("line")
      .attr("class", "shap-beeswarm-axis-border")
      .attr("x1", 0)
      .attr("y1", innerH)
      .attr("x2", innerW)
      .attr("y2", innerH);

    g.append("line")
      .attr("class", "shap-beeswarm-axis-border")
      .attr("x1", 0)
      .attr("y1", 0)
      .attr("x2", 0)
      .attr("y2", innerH);

    const plot = g.append("g").attr("clip-path", "url(#shap-beeswarm-plot-clip)");

    if (vertical) {
      const yScale = d3
        .scaleBand<string>()
        .domain(features.map((f) => f.name))
        .range([0, innerH])
        .padding(0.18);

      const xScale = d3
        .scaleLinear()
        .domain(shapScaleConfig.domain)
        .range([0, innerW])
        .nice();

      plot
        .append("line")
        .attr("x1", xScale(0))
        .attr("x2", xScale(0))
        .attr("y1", 0)
        .attr("y2", innerH)
        .attr("class", "shap-beeswarm-zero-line");

      g.append("g")
        .attr("class", "shap-beeswarm-axis shap-beeswarm-x-axis")
        .attr("transform", `translate(0,${innerH})`)
        .call(d3.axisBottom(xScale).ticks(5));

      g.append("text")
        .attr("class", "shap-beeswarm-x-label")
        .attr("x", innerW / 2)
        .attr("y", innerH + 44)
        .attr("text-anchor", "middle")
        .text(`Effect on ${targetUnit()}`);

      g.append("g")
        .attr("class", "shap-beeswarm-axis shap-beeswarm-y-axis")
        .call(
          d3.axisLeft(yScale).tickFormat((d) => rankedFeatureAxisLabel(features, d))
        )
        .selectAll("text")
        .attr("class", "shap-beeswarm-axis-label");

      for (const feat of features) {
        const centerY = (yScale(feat.name) ?? 0) + yScale.bandwidth() / 2;
        const rowHalfHeight = Math.max(4, yScale.bandwidth() / 2 - POINT_RADIUS - 1);
        const nodes = layoutRowBeeswarm(feat.points, centerY, rowHalfHeight, (pt) =>
          mapShapToPlotX(pt.shap, xScale, clipLow, clipHigh, innerW)
        );

        plot
          .selectAll(null)
          .data(nodes)
          .enter()
          .append("circle")
          .attr("class", (d) =>
            d.isOutlier ? "shap-beeswarm-point shap-beeswarm-point-outlier" : "shap-beeswarm-point"
          )
          .attr("cx", (d) => d.x)
          .attr("cy", (d) => d.y)
          .attr("r", POINT_RADIUS)
          .attr("fill", (d) => colorScale(d.norm))
          .append("title")
          .text((d) => {
            const valueText = d.value == null ? "—" : d.value.toFixed(2);
            const shapNote = d.isOutlier ? " (outside scale)" : "";
            return `${feat.label}\nSHAP: ${d.shap.toFixed(3)}${shapNote}\nValue: ${valueText}`;
          });
      }
    } else {
      const xScale = d3
        .scaleBand<string>()
        .domain(features.map((f) => f.name))
        .range([0, innerW])
        .padding(0.2);

      const yScale = d3
        .scaleLinear()
        .domain(shapScaleConfig.domain)
        .range([innerH, 0])
        .nice();

      plot
        .append("line")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("y1", yScale(0))
        .attr("y2", yScale(0))
        .attr("class", "shap-beeswarm-zero-line");

      g.append("g")
        .attr("class", "shap-beeswarm-axis shap-beeswarm-y-axis")
        .call(d3.axisLeft(yScale).ticks(5));

      g.append("text")
        .attr("class", "shap-beeswarm-y-label")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerH / 2)
        .attr("y", -44)
        .attr("text-anchor", "middle")
        .text(`Effect on ${targetUnit()}`);

      const xAxis = g
        .append("g")
        .attr("class", "shap-beeswarm-axis shap-beeswarm-x-axis")
        .attr("transform", `translate(0,${innerH})`)
        .call(
          d3.axisBottom(xScale).tickFormat((d) => rankedFeatureAxisLabel(features, d))
        );

      xAxis
        .selectAll("text")
        .attr("class", "shap-beeswarm-axis-label")
        .attr("transform", "rotate(-45)")
        .style("text-anchor", "end")
        .attr("dx", "-0.35em")
        .attr("dy", "0.25em");

      for (const feat of features) {
        const centerX = (xScale(feat.name) ?? 0) + xScale.bandwidth() / 2;
        const columnHalfWidth = Math.max(4, xScale.bandwidth() / 2 - POINT_RADIUS - 1);
        const nodes = layoutColumnBeeswarm(feat.points, centerX, columnHalfWidth, (pt) =>
          mapShapToPlotY(pt.shap, yScale, clipLow, clipHigh, innerH)
        );

        plot
          .selectAll(null)
          .data(nodes)
          .enter()
          .append("circle")
          .attr("class", (d) =>
            d.isOutlier ? "shap-beeswarm-point shap-beeswarm-point-outlier" : "shap-beeswarm-point"
          )
          .attr("cx", (d) => d.x)
          .attr("cy", (d) => d.y)
          .attr("r", POINT_RADIUS)
          .attr("fill", (d) => colorScale(d.norm))
          .append("title")
          .text((d) => {
            const valueText = d.value == null ? "—" : d.value.toFixed(2);
            const shapNote = d.isOutlier ? " (outside scale)" : "";
            return `${feat.label}\nSHAP: ${d.shap.toFixed(3)}${shapNote}\nValue: ${valueText}`;
          });
      }
    }

    logDebug("ShapBeeswarm: rendered", { featureCount: features.length, width, vertical });
  });

  onCleanup(() => {
    if (svgRef) d3.select(svgRef).selectAll("*").remove();
  });

  return (
    <div
      class={`shap-beeswarm-container${isVerticalLayout() ? " shap-beeswarm-container--vertical" : ""}`}
      ref={containerRef}
    >
      <Show when={props.title}>
        <h4 class="shap-beeswarm-title">{props.title}</h4>
      </Show>
      <Show when={props.features?.length > 0} fallback={<p class="performance-drivers-empty">No SHAP data for this cell.</p>}>
        <svg ref={svgRef} class="shap-beeswarm-svg" role="img" aria-label="SHAP beeswarm plot" />
      </Show>
    </div>
  );
}
